import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiErrorDetail, AUTH_TOKEN_KEY } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const { t } = useTranslation();
  const [user, setUser] = useState(null); // null = checking, false = not authenticated, object = authenticated
  const [loading, setLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      const { access_token, ...userData } = data;
      if (access_token) localStorage.setItem(AUTH_TOKEN_KEY, access_token);
      setUser(userData);
      return { success: true, user: userData };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail, t) || e.message };
    }
  };

  const register = async (email, password, role) => {
    try {
      const origin_url = window.location.origin;
      const { data } = await api.post("/auth/register", { email, password, role, origin_url });
      const { access_token, ...userData } = data;
      if (access_token) localStorage.setItem(AUTH_TOKEN_KEY, access_token);
      setUser(userData);
      return { success: true, user: userData };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail, t) || e.message };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      localStorage.removeItem(AUTH_TOKEN_KEY);
      setUser(false);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
